// src/lib/travelHealth/coAdmin/coAdminEngine.ts
//
// Pure function — no React, no Zustand, no side effects. Clinical rules live
// entirely in coAdminRules.ts; this file contains no hardcoded product
// names, rule text, or clinical values.

import { PGD_IDS, PRODUCT_META, type PgdId } from "../pgds/pgdIds";
import { CO_ADMIN_RULES, type CoAdminRuleDefinition } from "./coAdminRules";
import type { CoAdminConstraint } from "../types";

const ANY_OTHER_PRODUCT_PGD_ID = "multiple";
const ANY_OTHER_PRODUCT_DISPLAY_NAME = "Other basket products";

function isKnownPgdId(pgdId: string): pgdId is PgdId {
  return Object.prototype.hasOwnProperty.call(PRODUCT_META, pgdId);
}

function formatDateDDMMYYYY(dateIso: string): string {
  const [year, month, day] = dateIso.split("-");
  if (!year || !month || !day) return dateIso;
  return `${day}/${month}/${year}`;
}

function resolveTemplate(template: string, departureDate: string): string {
  return template.replace(
    /\{departureDate\}/g,
    formatDateDDMMYYYY(departureDate),
  );
}

function displayNameFor(pgdId: string): string {
  return isKnownPgdId(pgdId) ? PRODUCT_META[pgdId].displayName : pgdId;
}

function ruleMatches(
  rule: CoAdminRuleDefinition,
  basketPgdIds: string[],
): boolean {
  if (rule.pgdIdB !== null) {
    return (
      basketPgdIds.includes(rule.pgdIdA) && basketPgdIds.includes(rule.pgdIdB)
    );
  }

  if (rule.ruleId === "hep-b-heplisav-any-vaccine") {
    if (!basketPgdIds.includes(PGD_IDS.HEP_B)) return false;
    return basketPgdIds.some(
      (pgdId) =>
        pgdId !== PGD_IDS.HEP_B &&
        isKnownPgdId(pgdId) &&
        PRODUCT_META[pgdId].isVaccine,
    );
  }

  if (rule.ruleId === "chikungunya-any-vaccine") {
    if (!basketPgdIds.includes(PGD_IDS.CHIKUNGUNYA)) return false;
    return basketPgdIds.some((pgdId) => pgdId !== PGD_IDS.CHIKUNGUNYA);
  }

  return false;
}

export function evaluateCoAdminMatrix(
  basketPgdIds: string[],
  departureDate: string,
): {
  constraints: CoAdminConstraint[];
} {
  if (basketPgdIds.length < 2) {
    return { constraints: [] };
  }

  const constraints: CoAdminConstraint[] = [];

  for (const rule of CO_ADMIN_RULES) {
    if (!ruleMatches(rule, basketPgdIds)) continue;

    const pgdIdB = rule.pgdIdB ?? ANY_OTHER_PRODUCT_PGD_ID;
    const productNameB =
      rule.pgdIdB === null
        ? ANY_OTHER_PRODUCT_DISPLAY_NAME
        : displayNameFor(rule.pgdIdB);

    const constraint: CoAdminConstraint = {
      pgdIdA: rule.pgdIdA,
      pgdIdB,
      productNameA: displayNameFor(rule.pgdIdA),
      productNameB,
      rule: resolveTemplate(rule.ruleText, departureDate),
    };

    if (rule.schedulingNote) {
      constraint.schedulingRecommendation = resolveTemplate(
        rule.schedulingNote,
        departureDate,
      );
    }

    constraints.push(constraint);
  }

  return { constraints };
}
